import { Smartphone, MessageCircle, Layers, WashingMachine, Bell, LayoutDashboard, Check } from "lucide-react";
import { FlowDrop } from "@/components/washflow/Logo";

const steps = [
  { icon: Smartphone, title: "Pickup agent app", text: "Your agent counts the clothes with the customer, confirms the order, and takes payment — all on one screen." },
  { icon: MessageCircle, title: "Automatic receipts", text: "The customer instantly gets a WhatsApp and email receipt with every item listed and a delivery estimate." },
  { icon: Layers, title: "Smart order queue", text: "Orders are queued by colour and category before the clothes even arrive at your shop." },
  { icon: WashingMachine, title: "Arrival & wash start", text: "Staff mark when clothes arrive and when washing starts — one tap, no paperwork." },
  { icon: Bell, title: "Real-time notifications", text: "Customers and you get notified at every stage: received, washing, ready, delivered." },
  { icon: LayoutDashboard, title: "Live owner dashboard", text: "See today's orders, revenue, and pending work from your phone — anywhere in Ghana." },
];

export default function Phase1() {
  return (
    <section id="solution" data-testid="solution-phase1-section" className="bg-white py-16 sm:py-24">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          <div>
            <span className="inline-block text-xs font-bold uppercase tracking-widest text-[#1ABC9C] bg-[#E8F8F5] px-4 py-1.5 rounded-full mb-4">The Solution — Phase 1</span>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#2C3E50]">
              Operations: every garment tracked, every customer informed
            </h2>
            <p className="mt-4 text-lg text-[#5D6D7E] leading-relaxed">
              Phase 1 runs your day-to-day work. Simple steps your staff can learn in one afternoon:
            </p>
            <div className="mt-8 space-y-5">
              {steps.map((s, i) => (
                <div key={i} data-testid={`phase1-step-${i}`} className="flex gap-4 items-start">
                  <div className="flex flex-col items-center">
                    <span className="w-11 h-11 rounded-full bg-[#1B4F72] flex items-center justify-center shrink-0">
                      <s.icon className="w-5 h-5 text-[#1ABC9C]" />
                    </span>
                    {i < steps.length - 1 && <span className="w-0.5 flex-1 min-h-4 bg-[#AED6F1] mt-1" />}
                  </div>
                  <div className="pb-2">
                    <h3 className="font-display text-lg font-semibold text-[#2C3E50]">
                      <span className="text-[#1ABC9C] mr-2">{i + 1}.</span>{s.title}
                    </h3>
                    <p className="mt-1 text-base text-[#5D6D7E] leading-relaxed">{s.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="lg:sticky lg:top-24">
            <div data-testid="whatsapp-demo-modal" className="mx-auto max-w-sm bg-[#0B141A] rounded-[2.5rem] p-3 shadow-2xl">
              <div className="bg-[#075E54] rounded-t-[2rem] px-5 py-4 flex items-center gap-3">
                <FlowDrop size={36} />
                <div>
                  <p className="text-white font-semibold text-sm">WashFlow Receipts</p>
                  <p className="text-[#AED6F1] text-xs">online</p>
                </div>
              </div>
              <div className="bg-[#0B141A] bg-opacity-100 px-4 py-5 rounded-b-[2rem] space-y-3" style={{ backgroundImage: "radial-gradient(rgba(255,255,255,0.04) 1px, transparent 1px)", backgroundSize: "16px 16px" }}>
                <div className="bg-[#005C4B] text-white rounded-2xl rounded-tr-sm p-4 text-sm leading-relaxed max-w-[90%] ml-auto shadow">
                  <p className="font-bold mb-1">Order #WF-1042 confirmed</p>
                  <p>Hello Ama! We received your order:</p>
                  <ul className="mt-2 space-y-1 text-[#D1F2EB]">
                    <li className="flex items-center gap-1.5"><Check className="w-3.5 h-3.5" /> 8 shirts (whites)</li>
                    <li className="flex items-center gap-1.5"><Check className="w-3.5 h-3.5" /> 4 trousers (darks)</li>
                    <li className="flex items-center gap-1.5"><Check className="w-3.5 h-3.5" /> 2 kente wraps (delicates)</li>
                  </ul>
                  <p className="mt-2">Total: <span className="font-bold">GHS 145.00</span> — Paid</p>
                  <p className="mt-1">Ready by: <span className="font-bold">Thursday, 4:00 PM</span></p>
                  <p className="mt-2 text-[#AED6F1] underline">Track your order live</p>
                  <p className="text-right text-[10px] text-white/60 mt-1">10:32 AM</p>
                </div>
                <div className="bg-[#005C4B] text-white rounded-2xl rounded-tr-sm p-4 text-sm max-w-[90%] ml-auto shadow">
                  <p>Your clothes have arrived at our shop and washing has started. We'll message you when they're ready!</p>
                  <p className="text-right text-[10px] text-white/60 mt-1">12:15 PM</p>
                </div>
                <p className="text-center text-xs text-white/40 pt-1">Sent automatically — no staff effort needed</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
